/*
 * DependentRepeater
 * -----------------
 * Dependents are de-duplicated across applicants. Each dependent record lives
 * on its "owning" applicant with the shape:
 *   { id, name, age, sharedWith: [applicantId, ...] }
 *
 * `sharedWith` lists OTHER applicants who also share this dependent. The
 * owning applicant is always implied (never listed in sharedWith).
 *
 * Behaviour:
 * - Owning applicant: lists their dependents directly and can add/remove.
 * - Non-owning applicants: above their own list we render a "Dependents
 *   already declared by [other applicants]" block with a per-row checkbox
 *   that toggles inclusion in this applicant's sharedWith array. The
 *   dependent's name and age are shown so it's easy to identify each one.
 * - Then below: this applicant's own (unique) dependents.
 */
import React from 'react';
import TextField from './TextField';
import { applicantDisplayName } from '../steps.config';

export const makeDependentId = () => {
  if (typeof window !== 'undefined' && window.crypto && window.crypto.randomUUID) {
    try {
      return window.crypto.randomUUID();
    } catch (e) {
      // fall through
    }
  }
  return `dep_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

class DependentRepeater extends React.Component {
  ownDependents = () => {
    const { applicant } = this.props;
    return Array.isArray(applicant.dependants) ? applicant.dependants : [];
  };

  // List of dependents belonging to OTHER applicants that are visible to
  // this applicant as potential shared dependents.
  sharedFromOthers = () => {
    const { applicants, applicant } = this.props;
    const out = [];
    applicants.forEach((other, idx) => {
      if (other.id === applicant.id) return;
      const list = Array.isArray(other.dependants) ? other.dependants : [];
      list.forEach((dep) => {
        out.push({
          ownerId: other.id,
          ownerName: applicantDisplayName(other, idx),
          dependent: dep,
        });
      });
    });
    return out;
  };

  isSharedWithMe = (dep) => {
    const me = this.props.applicant.id;
    return Array.isArray(dep.sharedWith) && dep.sharedWith.includes(me);
  };

  toggleShared = (ownerId, depId) => {
    const me = this.props.applicant.id;
    const { applicants, onUpdateApplicant } = this.props;
    const owner = applicants.find((a) => a.id === ownerId);
    if (!owner) return;
    const list = (owner.dependants || []).map((d) => {
      if (d.id !== depId) return d;
      const current = Array.isArray(d.sharedWith) ? d.sharedWith : [];
      const next = current.includes(me)
        ? current.filter((id) => id !== me)
        : [...current, me];
      return { ...d, sharedWith: next };
    });
    onUpdateApplicant(ownerId, { dependants: list });
  };

  addDependent = () => {
    const { applicant, onUpdateApplicant } = this.props;
    const list = [
      ...this.ownDependents(),
      { id: makeDependentId(), name: '', age: '', sharedWith: [] },
    ];
    onUpdateApplicant(applicant.id, { dependants: list });
  };

  removeDependent = (depId) => {
    const { applicant, onUpdateApplicant } = this.props;
    const list = this.ownDependents().filter((d) => d.id !== depId);
    onUpdateApplicant(applicant.id, { dependants: list });
  };

  patchField = (depId, field, value) => {
    const { applicant, onUpdateApplicant } = this.props;
    const cleaned =
      field === 'age' ? (value || '').replace(/\D/g, '').slice(0, 2) : value;
    const list = this.ownDependents().map((d) =>
      d.id === depId ? { ...d, [field]: cleaned } : d
    );
    onUpdateApplicant(applicant.id, { dependants: list });
  };

  render() {
    const shared = this.sharedFromOthers();
    const own = this.ownDependents();
    const totalForThisApplicant =
      own.length + shared.filter(({ dependent }) => this.isSharedWithMe(dependent)).length;

    return (
      <div className="space-y-5">
        <p className="text-sm text-[#6B7280]">
          Tell us about any children or other people who financially depend on this applicant.
          You currently have <strong className="text-[#1F2937]">{totalForThisApplicant}</strong>{' '}
          {totalForThisApplicant === 1 ? 'dependent' : 'dependents'} recorded for this person.
        </p>

        {/* Shared-with picker for dependents belonging to other applicants */}
        {shared.length > 0 ? (
          <div className="rounded-xl border border-[#E5E7EB] bg-[#F9FAFB] p-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280] mb-2">
              Already declared by other applicants
            </p>
            <p className="text-xs text-[#6B7280] mb-3">
              Tick any that are also a dependent of this applicant - we'll record them as shared,
              not duplicated.
            </p>
            <ul className="space-y-2">
              {shared.map(({ ownerId, ownerName, dependent }) => {
                const isMine = this.isSharedWithMe(dependent);
                return (
                  <li key={`${ownerId}-${dependent.id}`}>
                    <button
                      type="button"
                      onClick={() => this.toggleShared(ownerId, dependent.id)}
                      className="w-full flex items-center gap-3 rounded-lg border-2 px-3 py-2 text-left transition-colors"
                      style={{
                        borderColor: isMine ? '#7C5CFC' : '#D1D5DB',
                        backgroundColor: isMine ? '#EFEAFE' : '#FFFFFF',
                        color: isMine ? '#5B3FE0' : '#1F2937',
                      }}
                    >
                      <span
                        className="inline-flex items-center justify-center h-5 w-5 rounded border flex-shrink-0"
                        style={{
                          borderColor: isMine ? '#7C5CFC' : '#9CA3AF',
                          backgroundColor: isMine ? '#7C5CFC' : '#FFFFFF',
                        }}
                      >
                        {isMine ? (
                          <svg className="h-3 w-3 text-white" viewBox="0 0 20 20" fill="currentColor">
                            <path
                              fillRule="evenodd"
                              d="M16.7 5.3a1 1 0 010 1.4l-7.5 7.5a1 1 0 01-1.4 0L3.3 9.7a1 1 0 011.4-1.4l3.8 3.8 6.8-6.8a1 1 0 011.4 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        ) : null}
                      </span>
                      <div className="flex-1 text-sm min-w-0">
                        <div className="font-medium truncate">
                          {dependent.name ? dependent.name : 'Unnamed dependent'}
                          <span className="ml-2 font-normal text-[#6B7280]">
                            (age {dependent.age || '-'})
                          </span>
                        </div>
                        <div className="text-xs text-[#6B7280] truncate">
                          declared by {ownerName}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ) : null}

        {/* Own dependents */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
              {shared.length > 0 ? 'Other dependents (not shared)' : 'Dependents'}
            </p>
            <span className="text-xs text-[#6B7280]">{own.length} added</span>
          </div>

          {own.length === 0 ? (
            <div className="rounded-xl border-2 border-dashed border-[#D1D5DB] bg-[#F9FAFB] px-4 py-5 text-center mb-3">
              <p className="text-sm text-[#6B7280]">
                No dependents added{shared.length > 0 ? ' yet.' : '.'}
              </p>
            </div>
          ) : (
            <ul className="space-y-2 mb-3">
              {own.map((dep, depIdx) => {
                const errs = this.props.errors || {};
                return (
                  <li
                    key={dep.id}
                    className="rounded-xl border border-[#E5E7EB] bg-white p-3"
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <span className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
                        {dep.name || 'New dependent'}
                      </span>
                      <button
                        type="button"
                        onClick={() => this.removeDependent(dep.id)}
                        className="text-xs font-semibold text-[#DC2626] hover:underline whitespace-nowrap"
                      >
                        Remove
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px] gap-3">
                      <TextField
                        label="Name"
                        required
                        value={dep.name}
                        onChange={(v) => this.patchField(dep.id, 'name', v)}
                        placeholder="e.g. Olivia"
                        autoComplete="off"
                        fieldId={`general.dependants.${depIdx}.name`}
                        error={errs[`general.dependants.${depIdx}.name`]}
                      />
                      <TextField
                        label="Age"
                        required
                        inputMode="numeric"
                        value={dep.age}
                        onChange={(v) => this.patchField(dep.id, 'age', v)}
                        maxLength={2}
                        fieldId={`general.dependants.${depIdx}.age`}
                        error={errs[`general.dependants.${depIdx}.age`]}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          <button
            type="button"
            onClick={this.addDependent}
            className="w-full rounded-xl border-2 border-dashed py-2.5 text-sm font-semibold"
            style={{ borderColor: '#7C5CFC', color: '#5B3FE0', backgroundColor: '#FFFFFF' }}
          >
            + Add a dependent
          </button>
        </div>
      </div>
    );
  }
}

export default DependentRepeater;
